import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../common/user";
import { wlCreatorService } from "../../../services/admin/wlCreator.service";
import {
	setGeneralProgressHide,
	setGeneralProgressShow,
  setGeneralSnackbarState,
} from "../../common/ui";


export type payloadList = {
	page: string | number | undefined;
	perPage: string | number | undefined;
	filter: {
		nameServerStatus?: string,
		createdByIds?: [],
	  trackerIds?: [],
	  search?: string,
  }
};

export type payloadFilterCreator = {
  page: string | number | undefined;
  search: string | null;
  perPage: string | number | undefined;
}

export interface IFilterCreatorData {
  list: {
    id: string | number;
    value: string;
  }[];
  totalPages: number;
}

export type columnsItem = {
  name: string;
  type: string;
  sort: boolean;
  match?: boolean;
};

export type listItem = {
  [key: string]: any;
};

export interface ITableData {
  list: listItem[];
  columns: columnsItem[];
	totalEntries: number;
	startRowNum: number;
	totalPages: number;
	endRowNum: number;
}

export type wlCreatorSliceState = {
  list: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  createOptions: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  create: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  filterCreator: {
    data: IFilterCreatorData | null,
    error: any,
    isFetching: boolean,
  },
  themes: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  themeUpdate: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  imagesUpload: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  marketingConfig: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  updateMarketing: {
    data: any,
    error: any,
    isFetching: boolean,
  },
};

const initialState: wlCreatorSliceState = {
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
  createOptions: {
    data: null,
    error: null,
    isFetching: false,
  },
  create: {
    data: null,
    error: null,
    isFetching: false,
  },
  filterCreator: {
    data: null,
    error: null,
    isFetching: false,
  },
  themes: {
    data: null,
    error: null,
    isFetching: false,
  },
  themeUpdate: {
    data: null,
    error: null,
    isFetching: false,
  },
  imagesUpload: {
    data: null,
    error: null,
    isFetching: false,
  },
  marketingConfig: {
    data: null,
    error: null,
    isFetching: false,
  },
  updateMarketing: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getListData: any = createAsyncThunk(
  'admin/flatFee/geListData',
  async (payload: payloadList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.getList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getFilterCreatorData: any = createAsyncThunk(
  "admin/wlCreator/getFilterCreatorData",
  async (payload: payloadFilterCreator, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());

    try {
      const response = await wlCreatorService.getFilterCreator(payload);
      const data = await response.json();

      if (data.hasOwnProperty("authorized") && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem("user");
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data);
      }

      dispatch(setGeneralProgressHide());

      return {
        payload: {
          list: data.list,
          totalPages: data.totalPages,
        },
      };
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const getThemesData: any = createAsyncThunk(
  'admin/wlCreator/getThemesData',
  async (platformId: string | number, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.getThemes(platformId);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const updateThemeData: any = createAsyncThunk(
  'admin/wlCreator/updateThemeData',
  async (payload: { platformId: string | number; data: any}, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.updateTheme(payload.platformId, payload.data);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const uploadImagesData: any = createAsyncThunk(
  'admin/wlCreator/uploadImagesData',
  async (payload: { platformId: string | number; data: any}, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.uploadImages(payload.platformId, payload.data);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getCreateOptionsData: any = createAsyncThunk(
  'admin/wlCreator/getCreateOptions',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.getCreateOptions();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const createPlatformData: any = createAsyncThunk(
  'admin/wlCreator/create',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.create(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.wlCreated',
          })
        );
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getMarketingConfigData: any = createAsyncThunk(
  'admin/wlCreator/getMarketingConfig',
  async (platformId: string, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.getMarketingConfig(platformId);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data);
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const updateMarketingConfigData: any = createAsyncThunk(
  'admin/wlCreator/updateMarketingConfig',
  async ({ platformId, payload }: { platformId: string; payload: any }, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.updateMarketingConfig(platformId, payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        );
        return rejectWithValue(data);
      }

      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'success',
          message: 'success',
          messageKey: 'common.messages.success',
        })
      );

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const wlCreatorSlice = createSlice({
  name: 'wlCreator',
  initialState: initialState,
  reducers: {
    clearListState(state) {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = null;
    },
    clearFilterCreatorState(state) {
      state.filterCreator.error = null;
      state.filterCreator.isFetching = false;
      state.filterCreator.data = null;
    },
    clearThemesState(state) {
      state.themes.error = null;
      state.themes.isFetching = false;
      state.themes.data = null;
    },
    clearThemeUpdateState(state) {
      state.themeUpdate.error = null;
      state.themeUpdate.isFetching = false;
      state.themeUpdate.data = null;
    },
    clearImagesUploadState(state) {
      state.imagesUpload.error = null;
      state.imagesUpload.isFetching = false;
      state.imagesUpload.data = null;
    },
    clearCreateOptionsState(state) {
      state.createOptions.error = null;
      state.createOptions.isFetching = false;
      state.createOptions.data = null;
    },
    clearCreateState(state) {
      state.create.error = null;
      state.create.isFetching = false;
      state.create.data = null;
    },
    clearMarketingConfigState(state) {
      state.marketingConfig.error = null;
      state.marketingConfig.isFetching = false;
      state.marketingConfig.data = null;
    },
    clearUpdateMarketingState(state) {
      state.updateMarketing.error = null;
      state.updateMarketing.isFetching = false;
      state.updateMarketing.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<wlCreatorSliceState>>) => {
    builder.addCase(getListData.pending, (state ) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getListData.fulfilled, (state , action) => {
      state.list.error = null;
      state.list.isFetching = false;
	    state.list.data = action.payload;
    });
    builder.addCase(getListData.rejected, (state , action) => {
      state.list.isFetching = false;
      state.list.error = action.payload;
      state.list.data = null;
    });

    builder.addCase(getFilterCreatorData.pending, (state ) => {
      state.filterCreator.isFetching = true;
      state.filterCreator.error = null;
    });
    builder.addCase(getFilterCreatorData.fulfilled, (state , action) => {
      state.filterCreator.error = null;
      state.filterCreator.isFetching = false;
	    state.filterCreator.data = action.payload;
    });
    builder.addCase(getFilterCreatorData.rejected, (state , action) => {
      state.filterCreator.isFetching = false;
      state.filterCreator.error = action.payload;
      state.filterCreator.data = null;
    });

    builder.addCase(getThemesData.pending, (state ) => {
      state.themes.isFetching = true;
      state.themes.error = null;
    });
    builder.addCase(getThemesData.fulfilled, (state , action) => {
      state.themes.error = null;
      state.themes.isFetching = false;
	    state.themes.data = action.payload;
    });
    builder.addCase(getThemesData.rejected, (state , action) => {
      state.themes.isFetching = false;
      state.themes.error = action.payload;
      state.themes.data = null;
    });

    builder.addCase(updateThemeData.pending, (state ) => {
      state.themeUpdate.isFetching = true;
      state.themeUpdate.error = null;
    });
    builder.addCase(updateThemeData.fulfilled, (state , action) => {
      state.themeUpdate.error = null;
      state.themeUpdate.isFetching = false;
	    state.themeUpdate.data = action.payload;
    });
    builder.addCase(updateThemeData.rejected, (state , action) => {
      state.themeUpdate.isFetching = false;
      state.themeUpdate.error = action.payload;
      state.themeUpdate.data = null;
    });

    builder.addCase(uploadImagesData.pending, (state ) => {
      state.imagesUpload.isFetching = true;
      state.imagesUpload.error = null;
    });
    builder.addCase(uploadImagesData.fulfilled, (state , action) => {
      state.imagesUpload.error = null;
      state.imagesUpload.isFetching = false;
	    state.imagesUpload.data = action.payload;
    });
    builder.addCase(uploadImagesData.rejected, (state , action) => {
      state.imagesUpload.isFetching = false;
      state.imagesUpload.error = action.payload;
      state.imagesUpload.data = null;
    });

    builder.addCase(getCreateOptionsData.pending, (state) => {
      state.createOptions.isFetching = true;
      state.createOptions.error = null;
    });
    builder.addCase(getCreateOptionsData.fulfilled, (state, action) => {
      state.createOptions.error = null;
      state.createOptions.isFetching = false;
      state.createOptions.data = action.payload;
    });
    builder.addCase(getCreateOptionsData.rejected, (state, action) => {
      state.createOptions.isFetching = false;
      state.createOptions.error = action.payload;
      state.createOptions.data = null;
    });

    builder.addCase(createPlatformData.pending, (state) => {
      state.create.isFetching = true;
      state.create.error = null;
    });
    builder.addCase(createPlatformData.fulfilled, (state, action) => {
      state.create.error = null;
      state.create.isFetching = false;
      state.create.data = action.payload;
    });
    builder.addCase(createPlatformData.rejected, (state, action) => {
      state.create.isFetching = false;
      state.create.error = action.payload;
      state.create.data = null;
    });

    builder.addCase(getMarketingConfigData.pending, (state) => {
      state.marketingConfig.isFetching = true;
      state.marketingConfig.error = null;
    });
    builder.addCase(getMarketingConfigData.fulfilled, (state, action) => {
      state.marketingConfig.error = null;
      state.marketingConfig.isFetching = false;
      state.marketingConfig.data = action.payload;
    });
    builder.addCase(getMarketingConfigData.rejected, (state, action) => {
      state.marketingConfig.isFetching = false;
      state.marketingConfig.error = action.payload;
      state.marketingConfig.data = null;
    });

    builder.addCase(updateMarketingConfigData.pending, (state) => {
      state.updateMarketing.isFetching = true;
      state.updateMarketing.error = null;
    });
    builder.addCase(updateMarketingConfigData.fulfilled, (state, action) => {
      state.updateMarketing.error = null;
      state.updateMarketing.isFetching = false;
      state.updateMarketing.data = action.payload;
    });
    builder.addCase(updateMarketingConfigData.rejected, (state, action) => {
      state.updateMarketing.isFetching = false;
      state.updateMarketing.error = action.payload;
      state.updateMarketing.data = null;
    });
  },
});
export const {
  clearListState,
  clearFilterCreatorState,
  clearThemesState,
  clearThemeUpdateState,
  clearImagesUploadState,
  clearCreateOptionsState,
  clearCreateState,
  clearMarketingConfigState,
  clearUpdateMarketingState,
} = wlCreatorSlice.actions;

export default wlCreatorSlice.reducer;
